import _ from 'lodash';
import { type UnknownAction } from '@reduxjs/toolkit'

import {
  FETCH_COLLECTION_LISTS_REQUEST,
  FETCHING_COLLECTION_LISTS_SUCCEEDED,
  FETCHING_COLLECTION_LISTS_FAILED,
  FETCH_COLLECTIONS_REQUEST,
  FETCHING_COLLECTIONS_SUCCEEDED,
  INVALIDATE_COLLECTIONS,
  FETCHING_COLLECTIONS_FAILED,
  UPDATE_COLLECTION_REQUEST,
  UPDATING_COLLECTION_SUCCEEDED,
  UPDATING_COLLECTION_FAILED,
  UPDATE_COLLECTION_LIST_REQUEST,
  UPDATING_COLLECTION_LIST_FAILED,
  UPDATING_COLLECTION_LIST_SUCCEEDED,
  DELETE_COLLECTION_REQUEST,
  DELETING_COLLECTION_SUCCEEDED,
  DELETING_COLLECTION_FAILED,
  DELETE_COLLECTION_LIST_REQUEST,
  DELETING_COLLECTION_LIST_SUCCEEDED,
  DELETING_COLLECTION_LIST_FAILED,
  CREATE_COLLECTION_REQUEST,
  CREATING_COLLECTION_SUCCEEDED,
  CREATING_COLLECTION_FAILED,
  CREATE_COLLECTION_LIST_REQUEST,
  CREATING_COLLECTION_LIST_SUCCEEDED,
  CREATING_COLLECTION_LIST_FAILED,
  ADD_COLLECTION_TO_LIST_REQUEST,
  ADDING_COLLECTION_TO_LIST_SUCCEEDED,
  ADDING_COLLECTION_TO_LIST_FAILED,
  REMOVE_COLLECTION_FROM_LIST_REQUEST,
  REMOVING_COLLECTION_FROM_LIST_SUCCEEDED,
  REMOVING_COLLECTION_FROM_LIST_FAILED,
  INVALIDATE_COLLECTION_CREATION_STATE,
  INVALIDATE_COLLECTION_UPDATE_STATE,
  INVALIDATE_COLLECTION_DELETION_STATE,
  INVALIDATE_COLLECTION_MOVE_STATE,
  INVALIDATE_COLLECTION_LIST_CREATION_STATE,
  INVALIDATE_COLLECTION_LIST_UPDATE_STATE,
  INVALIDATE_COLLECTION_LIST_DELETION_STATE,
  FETCH_COLLECTION_REQUEST,
  FETCHING_COLLECTION_SUCCEEDED,
  FETCHING_COLLECTION_FAILED,
  FETCH_COLLECTION_SECTION_REQUEST,
  FETCHING_COLLECTION_SECTION_SUCCEEDED,
  FETCHING_COLLECTION_SECTION_FAILED,
  EDIT_COLLECTION_REQUEST,
  INVALIDATE_COLLECTION_EDIT_STATE,
  CREATE_COLLECTION_SECTION_REQUEST,
  UPDATE_COLLECTION_SECTION_REQUEST,
  REMOVE_COLLECTION_SECTION_REQUEST,
  COLLECTION_REMOVE_ASSETS,
  COLLECTION_REARRANGE_ASSETS,
  COLLECTION_ADD_ASSETS,
  COLLECTION_UPDATE_PROPERTY,
  TOGGLE_COLLECTION_TEMPLATE_REQUEST,
  TOGGLE_COLLECTION_INTERNAL_REQUEST,
  TOGGLING_COLLECTION_STATE_SUCCEEDED,
  TOGGLING_COLLECTION_STATE_FAILED,
  TOGGLE_COLLECTION_LIVE_REQUEST,
  MOVE_COLLECTION_SECTION_REQUEST,
  COLLECTION_UPDATE_ASSET_METADATA,
  FETCHING_COLLECTION_SHARED_CONTACTS_SUCCEEDED,
  FETCHING_COLLECTION_SHARED_CONTACTS_FAILED,
  FETCH_COLLECTION_SHARED_CONTACTS_REQUEST,
  FETCH_COLLECTION_COLLABORATORS_REQUEST,
  FETCHING_COLLECTION_COLLABORATORS_SUCCEDDED,
  FETCHING_COLLECTION_COLLABORATORS_FAILED,
  FETCH_COLLECTION_USER_ACCESS_REQUEST,
  FETCHING_COLLECTION_USER_ACCESS_FAILED,
  FETCHING_COLLECTION_USER_ACCESS_SUCCEDDED,
  UPDATE_COLLECTION_COLLABORATORS_REQUEST,
  UPDATING_COLLECTION_COLLABORATORS_SUCCEDDED,
  UPDATING_COLLECTION_COLLABORATORS_FAILED,
  INVALIDATE_COLLECTION_COLLABORATORS,
  ADDING_ASSETS_TO_COLLECTION_ON_SERVER_SUCCEDDED,
  ADD_ASSETS_TO_SECTION_ON_SERVER_SUCCEDDED,
  FETCHING_ASSETS_FROM_UNCATEGORIZED_COLLECTION_SUCCEDDED,
  FETCH_ASSETS_FROM_UNCATEGORIZED_COLLECTION,
  FETCHING_ASSETS_FROM_UNCATEGORIZED_COLLECTION_FAILED,
  FETCHING_ASSET_ANALYTICS_REQUEST,
  FETCHING_ASSET_ANALYTICS_SUCCEEDED,
  FETCHING_ASSET_ANALYTICS_FAILED,
  DELETE_UNCATEGORIZED_ASSET,
  DELETING_UNCATEGORIZED_ASSET_SUCCEEDED,
  DELETING_UNCATEGORIZED_ASSET_FAILED,
  INVALIDATE_UNCATEGORIZED_ASSET_DELETION,
  SORT_UNCATEGORIZED_COLLECTION_CHILDREN,
  REPLACE_SIMILAR_ASSET_SUCCEEDED,
} from '../actions/actionTypes';

import {
  AddAssetToCollectionRequest,
  AddAssetToCollectionResponse,
  AddCollectionToListErrorResponse,
  AddCollectionToListRequest,
  AddCollectionToListResponse,
  ArrangeAssetInCollectionRequest,
  Collection,
  CollectionAsset,
  CollectionCollaborate,
  CollectionEditState,
  CollectionList,
  CollectionSectionState,
  CreateCollectionErrorResponse,
  CreateCollectionListErrorResponse,
  CreateCollectionListResponse,
  CreateCollectionResponse,
  CreateCollectionSectionRequest,
  DeleteCollectionErrorResponse,
  DeleteCollectionListRequest,
  DeleteCollectionListResponse,
  DeleteCollectionRequest,
  DeleteCollectionResponse,
  DeleteUncategorizedAssetErrorResponse,
  DeleteUncategorizedAssetResponse,
  FetchAssetFromUncategorisedCollectionErrorResponse,
  FetchAssetFromUncategorisedCollectionResponse,
  FetchCollectionCollaboratorsErrorResponse,
  FetchCollectionCollaboratorsRequest,
  FetchCollectionCollaboratorsResponse,
  FetchCollectionErrorResponse,
  FetchCollectionResponse,
  FetchCollectionSectionErrorResponse,
  FetchCollectionSectionRequest,
  FetchCollectionSectionResponse,
  FetchCollectionSharedContactErrorResponse,
  FetchCollectionSharedContactRequest,
  FetchCollectionSharedContactResponse,
  FetchCollectionsRequest,
  FetchCollectionsResponse,
  FetchUserAccessResponse,
  MoveCollectionSectionRequest,
  RemoveAssetFromCollectionRequest,
  RemoveCollectionFromListErrorResponse,
  RemoveCollectionFromListRequest,
  RemoveCollectionFromListResponse,
  RenameCollectionSectionRequest,
  SortUncategorizedAssetsRequest,
  ToggleCollectionInternalRequest,
  ToggleCollectionLiveRequest,
  ToggleCollectionStateErrorResponse,
  ToggleCollectionStateResponse,
  ToggleCollectionTemplateRequest,
  UpdateAssetMetadataRequest,
  UpdateCollectionCollaboratorsErrorResponse,
  UpdateCollectionCollaboratorsRequest,
  UpdateCollectionCollaboratorsResponse,
  UpdateCollectionErrorResponse,
  UpdateCollectionListErrorResponse,
  UpdateCollectionListRequest,
  UpdateCollectionListResponse,
  UpdateCollectionRequest,
  UpdateCollectionResponse
} from '../../types/collections'
import { DEFAULT_COLLECTION_LIST } from '../../constants/collections';
import { nanoid } from 'nanoid';
import { Asset, FetchAssetAnalyticsErrorResponse, FetchAssetAnalyticsRequest, FetchAssetAnalyticsResponse, ReplaceSimilarAssetResponse } from 'src/types/assets';
import { getContactCardExists } from 'src/utils/collections.helper';

interface CollectionsState {
  lists: CollectionListsState
  loading: boolean
  items: Collection[],
  error: any,
  pagination: {
    currentPage: number,
    totalPages: number,
    recordsPerPage: number,
    totalRecordsCount: number,
    hasMoreItems: boolean
  },
  filtered: {
    [key: string]: CollectionItemsState;
  }
  creation: CollectionCreationState | null,
  update: {
    [key: string]: CollectionUpdateState;
  },
  deletion: {
    [key: string]: CollectionDeletionState;
  },
  move: {
    [key: string]: CollectionMoveState;
  },
  collection: {
    [key: string]: CollectionState;
  }
  edit: CollectionEditState | null,
  uncategorized: {
    loading: boolean
    items: UncategorizedAssetState[],
    error: any,
    sortBy: {
      type: string,
      order: 'asc' | 'desc',
    },
    pagination: {
      currentPage: number,
      totalPages: number,
      recordsPerPage: number,
      totalRecordsCount: number,
      hasMoreItems: boolean
    },
    deletion: {
      deleting: boolean;
      deleted: boolean;
      error?: any;
    },
  }
}

interface CollectionListsState {
  loading: boolean,
  items: CollectionList[],
  error: any,
  creation: CollectionListCreationState | null,
  update: {
    [key: string]: CollectionListUpdateState;
  },
  deletion: {
    [key: string]: CollectionListDeleteState;
  }
}

interface CollectionItemsState {
  loading: boolean,
  items: Collection[],
  error: any,
  pagination: {
    currentPage: number,
    totalPages: number,
    recordsPerPage: number,
    totalRecordsCount: number,
    hasMoreItems: boolean
  },
}

interface CollectionState {
  loading: boolean,
  loaded: boolean,
  item: CollectionEditState | null,
  recipients?: CollectionSharedContactsState,
  collaborators?: CollectionCollaboratorsState,
  error: any
}

interface CollectionCreationState {
  creating: boolean,
  created: boolean,
  item: Collection | null,
  error: any
}

interface CollectionUpdateState {
  id: string,
  updating: boolean,
  updated: boolean,
  item: Collection | null,
  error: any
}

interface CollectionDeletionState {
  id: string,
  deleting: boolean,
  deleted: boolean,
  error: any
}

interface CollectionMoveState {
  id: string,
  type: 'add' | 'remove',
  source?: string | null,
  target: CollectionList,
  collection: Collection,
  moving: boolean,
  moved: boolean,
  error?: any
}

//Collection List
interface CollectionListCreationState {
  creating: boolean,
  created: boolean,
  item: Collection | null,
  error: any
}

interface CollectionListUpdateState {
  id: string,
  updating: boolean,
  updated: boolean,
  item: CollectionList | null,
  error: any
}

interface CollectionListDeleteState {
  id: string,
  deleting: boolean,
  deleted: boolean,
  error: any
}

interface CollectionSharedContactsState {
  loading: boolean,
  loaded: boolean,
  items: any[],
  pagination: {
    currentPage: number,
    totalPages: number,
    recordsPerPage: number,
    totalRecordsCount: number,
    hasMoreItems: boolean
  },
  error?: any
}

interface CollectionCollaboratorsState {
  loading?: boolean,
  loaded?: boolean,
  updating?: boolean,
  updated?: boolean,
  items: CollectionCollaborate[],
  pagination?: {
    currentPage: number,
    totalPages: number,
    recordsPerPage: number,
    totalRecordsCount: number,
    hasMoreItems: boolean
  },
  error?: any
}

interface UncategorizedAssetState {
  id: string,
  asset: Asset,
  analytics: {
    loading: boolean,
    loaded: boolean,
    item?: any,
    error?: any
  }
}

const initialState: CollectionsState = {
  lists: {
    loading: false,
    items: [],
    error: null,
    creation: null,
    update: {},
    deletion: {}
  },
  loading: false,
  items: [],
  pagination: {
    currentPage: -1,
    totalPages: 0,
    recordsPerPage: 10,
    totalRecordsCount: 0,
    hasMoreItems: false
  },
  error: null,
  filtered: {},
  creation: null,
  update: {},
  deletion: {},
  move: {},
  collection: {},
  edit: null,
  uncategorized: {
    loading: false,
    items: [],
    error: null,
    sortBy: {
      type: 'name',
      order: 'asc',
    },
    pagination: {
      currentPage: -1,
      totalPages: 0,
      recordsPerPage: 10,
      totalRecordsCount: 0,
      hasMoreItems: false
    },
    deletion: {
      deleting: false,
      deleted: false
    }
  }
}

const initialCollectionItemsState: CollectionItemsState = {
  loading: false,
  items: [],
  error: null,
  pagination: {
    currentPage: -1,
    totalPages: 0,
    recordsPerPage: 10,
    totalRecordsCount: 0,
    hasMoreItems: false,
  }
}

const initialCollectionCreationState: CollectionCreationState = {
  creating: false,
  created: false,
  item: null,
  error: null
}

const initialCollectionUpdateState: CollectionUpdateState = {
  id: '',
  updating: false,
  updated: false,
  item: null,
  error: null
}

const initialCollectionDeletionState: CollectionDeletionState = {
  id: '',
  deleting: false,
  deleted: false,
  error: null
}

const initialCollectionListCreationState: CollectionListCreationState = {
  creating: false,
  created: false,
  item: null,
  error: null
}

const initialCollectionListUpdateState: CollectionListUpdateState = {
  id: '',
  updating: false,
  updated: false,
  item: null,
  error: null
}

const initialCollectionListDeletionState: CollectionListDeleteState = {
  id: '',
  deleting: false,
  deleted: false,
  error: null
}

const sortUncategorizedAssets = (assets, sortBy) => {
  switch(sortBy.type){
    case 'name': {
      return _.orderBy(assets, 'asset.name', sortBy.order);
    }
    case 'views': {
      return _.orderBy(assets, 'analytics.item.views', sortBy.order);
    }
    case 'shares': {
      return _.orderBy(assets, 'analytics.item.shares', sortBy.order);
    }
    case 'downloads': {
      return _.orderBy(assets, 'analytics.item.downloads', sortBy.order);
    }
    case 'timespent': {
      return _.orderBy(assets, 'analytics.item.totalTimeSpent', sortBy.order);
    }
    default:{
      return assets;
    }
  }
}

export function collectionsReducer(state: CollectionsState = initialState, action: UnknownAction): CollectionsState {
  switch (action.type) {
    case FETCH_COLLECTION_LISTS_REQUEST: {
      return {
        ...state,
        lists: {
          ...state.lists,
          loading: true
        }
      }
    }

    case FETCHING_COLLECTION_LISTS_SUCCEEDED: {
      const response = action.payload as CollectionList[];

      return {
        ...state,
        lists: {
          ...state.lists,
          loading: false,
          items: response,
          error: null
        }
      }
    }

    case FETCHING_COLLECTION_LISTS_FAILED: {
      const error = action.payload;

      return {
        ...state,
        lists: {
          ...state.lists,
          loading: false,
          error: error
        }
      }
    }

    case FETCH_COLLECTIONS_REQUEST: {
      const { list } = action.payload as FetchCollectionsRequest;

      if (list) {
        const listState = state.filtered[list] || initialCollectionItemsState;
        return {
          ...state,
          filtered: {
            ...state.filtered,
            [list]: {
              ...listState,
              loading: true
            }
          }
        }
      }

      return {
        ...state,
        loading: true
      }
    }

    case FETCHING_COLLECTIONS_SUCCEEDED: {
      const { list, response } = action.payload as FetchCollectionsResponse;

      let existingItems: Collection[] = state.items;
      if (list) {
        existingItems = _.get(state, `filtered['${list}'].items`) || [];
      }

      let records = response.records;
      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }

      records = _.uniqBy(records, 'id');

      if (list) {
        const listState = state.filtered[list] || initialCollectionItemsState;
        return {
          ...state,
          filtered: {
            ...state.filtered,
            [list]: {
              ...listState,
              loading: false,
              items: records,
              pagination: {
                currentPage: response.currentPage,
                totalPages: response.totalPages,
                recordsPerPage: response.recordsPerPage,
                totalRecordsCount: response.totalRecordsCount,
                hasMoreItems: response.currentPage < response.totalPages - 1
              }
            }
          }
        }
      }

      return {
        ...state,
        loading: false,
        items: records,
        pagination: {
          currentPage: response.currentPage,
          totalPages: response.totalPages,
          recordsPerPage: response.recordsPerPage,
          totalRecordsCount: response.totalRecordsCount,
          hasMoreItems: response.currentPage < response.totalPages - 1
        },
      }
    }

    case FETCHING_COLLECTIONS_FAILED: {
      const error = action.payload;
      return {
        ...state,
        loading: false,
        error: error
      }
    }

    case INVALIDATE_COLLECTIONS: {
      return {
        ...state,
        loading: false,
        items: [],
        pagination: {
          currentPage: -1,
          totalPages: 0,
          recordsPerPage: 10,
          totalRecordsCount: 0,
          hasMoreItems: false
        },
        error: null,
        filtered: {}
      }
    }

    case CREATE_COLLECTION_REQUEST: {
      return {
        ...state,
        creation: {
          ...initialCollectionCreationState,
          creating: true
        }
      }
    }

    case CREATING_COLLECTION_SUCCEEDED: {
      const { response } = action.payload as CreateCollectionResponse;
      const creationState = state.creation;
      if (!creationState) {
        return state;
      }

      return {
        ...state,
        creation: {
          ...creationState,
          creating: false,
          created: true,
          item: response,
          error: null
        }
      }
    }

    case CREATING_COLLECTION_FAILED: {
      const { error } = action.payload as CreateCollectionErrorResponse;
      const creationState = state.creation;
      if (!creationState) {
        return state;
      }

      return {
        ...state,
        creation: {
          ...creationState,
          creating: false,
          created: true,
          error: error
        }
      }
    }

    case UPDATE_COLLECTION_REQUEST: {
      const { id } = action.payload as UpdateCollectionRequest;
      let updateState = state.update[id] || initialCollectionUpdateState;

      updateState = {
        ...updateState,
        id,
        updating: true,
        updated: false,
        item: null,
        error: null
      }

      return {
        ...state,
        update: {
          ...state.update,
          [id]: updateState
        }
      }
    }

    case UPDATING_COLLECTION_SUCCEEDED: {
      const { response } = action.payload as UpdateCollectionResponse;
      const id = response.id;
      if (!id) {
        return state;
      }

      const updateState = state.update[id];
      if (!updateState) {
        return state;
      }

      let editState = state.edit;
      if (editState?.id !== id) {
        editState = null;
      }

      let collectionState = state.collection[id];
      if (collectionState) {
        const sections = (response.sections || []).map((section, index): CollectionSectionState => {
          const existingSection = _.get(editState, `sections[${index}]`);

          if (existingSection) {
            return {
              uid: existingSection.uid,
              item: {
                ...section,
                collection: id,
                entities: existingSection.item?.entities || []
              },
              loading: existingSection.loading,
              loaded: existingSection.loaded,
              error: null
            }
          }

          return {
            uid: nanoid(6),
            loading: true,
            loaded: false,
            item: {
              ...section,
              collection: id
            },
            error: null
          }
        }) as CollectionSectionState[];

        collectionState = {
          ...collectionState,
          item: {
            ...response,
            sections: sections || []
          },
        }
      }

      const items = state.items.map((item) => {
        if (item.id === id) {
          return response;
        }

        return item;
      });

      const filtered = Object.keys(state.filtered).reduce((acc: { [key: string]: CollectionItemsState }, key) => {
        const listState = state.filtered[key];
        const items = listState.items.map((item) => {
          if (item.id === id) {
            return response;
          }

          return item;
        });

        acc[key] = {
          ...listState,
          items
        }

        return acc;
      }, {});

      return {
        ...state,
        items,
        filtered,
        update: {
          ...state.update,
          [id]: {
            ...updateState,
            updating: false,
            updated: true,
            item: response,
            error: null
          }
        },
        collection: {
          ...state.collection,
          [id]: collectionState
        },
      }
    }

    case UPDATING_COLLECTION_FAILED: {
      const { id, error } = action.payload as UpdateCollectionErrorResponse;
      const updateState = state.update[id];
      if (!updateState) {
        return state;
      }

      return {
        ...state,
        update: {
          ...state.update,
          [id]: {
            ...updateState,
            updating: false,
            updated: false,
            error: error
          }
        }
      }
    }

    case DELETE_COLLECTION_REQUEST: {
      const { id } = action.payload as DeleteCollectionRequest;
      let deletionState = state.deletion[id] || initialCollectionDeletionState;

      deletionState = {
        ...deletionState,
        id,
        deleting: true,
        deleted: false,
        error: null
      }

      return {
        ...state,
        deletion: {
          ...state.deletion,
          [id]: deletionState
        }
      }
    }

    case DELETING_COLLECTION_SUCCEEDED: {
      const { id } = action.payload as DeleteCollectionResponse;
      const deletionState = state.deletion[id];
      if (!deletionState) {
        return state;
      }

      const items = state.items.filter((item) => {
        return item.id !== id;
      });

      const filtered = Object.keys(state.filtered).reduce((acc: { [key: string]: CollectionItemsState }, key) => {
        const listState = state.filtered[key];
        const items = listState.items.filter((item) => {
          return item.id !== id;
        });

        acc[key] = {
          ...listState,
          items
        }

        return acc;
      }, {});

      return {
        ...state,
        items,
        filtered,
        deletion: {
          ...state.deletion,
          [id]: {
            ...deletionState,
            deleting: false,
            deleted: true,
            error: null
          }
        }
      }
    }

    case DELETING_COLLECTION_FAILED: {
      const { id, error } = action.payload as DeleteCollectionErrorResponse;
      const deletionState = state.deletion[id];
      if (!deletionState) {
        return state;
      }

      return {
        ...state,
        deletion: {
          ...state.deletion,
          [id]: {
            ...deletionState,
            deleting: false,
            deleted: false,
            error: error
          }
        }
      }
    }

    //Fetch Collection 
    case FETCH_COLLECTION_REQUEST: {
      const id = action.payload as string;

      return {
        ...state,
        collection: {
          ...state.collection,
          [id]: {
            loading: true,
            loaded: false,
            item: null,
            error: null,
          }
        }
      }
    }

    case FETCHING_COLLECTION_SUCCEEDED: {
      const { response } = action.payload as FetchCollectionResponse;

      if (!response || !response.id) {
        return state;
      }

      const collectionState = state.collection[response.id];
      if (!collectionState) {
        return state;
      }

      const sections = (response.sections || []).map((section): CollectionSectionState => {
        return {
          uid: nanoid(6),
          loading: true,
          loaded: false,
          item: {
            ...section,
            collection: response.id
          },
          error: null
        }
      }) as CollectionSectionState[];

      const allEntities = _.get(response, "entities", []);
      const sortedEntities = _.sortBy(allEntities, ["order"]);

      return {
        ...state,
        collection: {
          ...state.collection,
          [response.id]: {
            loading: false,
            loaded: true,
            item: {
              ...response,
              entities: [...sortedEntities],
              sections: sections || [],
            },
            error: null,
          },
        },
      };
    }

    case FETCHING_COLLECTION_FAILED: {
      const { id, error } = action.payload as FetchCollectionErrorResponse;

      const collectionState = state.collection[id];
      if (!collectionState) {
        return state;
      }

      return {
        ...state,
        collection: {
          ...state.collection,
          [id]: {
            loading: false,
            loaded: false,
            item: null,
            error: error
          }
        }
      }
    }

    case FETCH_COLLECTION_SECTION_REQUEST: {
      const { collection, section } = action.payload as FetchCollectionSectionRequest;

      const collectionState = state.collection[collection];
      if (!collectionState || !collectionState.item) {
        return state;
      }

      const sections = (collectionState.item.sections || []).map((item) => {
        if (item.item?.id === section) {
          return {
            ...item,
            loading: true
          }
        }

        return item;
      }) as CollectionSectionState[];

      return {
        ...state,
        collection: {
          ...state.collection,
          [collection]: {
            ...collectionState,
            item: {
              ...collectionState.item,
              sections: sections
            }
          }
        }
      };
    }

    case FETCHING_COLLECTION_SECTION_SUCCEEDED: {
      const { collection, section, response } = action.payload as FetchCollectionSectionResponse;

      const collectionState = state.collection[collection];
      if (!collectionState || !collectionState.item) {
        return state;
      }

      const sections = (collectionState.item.sections || []).map((item) => {
        if (item.item?.id === section) {
          return {
            ...item,
            item: {
              ...item.item,
              collection: collection,
              entities: response.entities,
            },
            loading: false,
            loaded: true,
          }
        }

        return item;
      });

      let editState = state.edit;
      if (editState) {
        const editSections = (editState.sections || []).map((item) => {
          if (item.item?.id === section) {
            return {
              ...item,
              item: {
                ...item.item,
                collection: collection,
                entities: response.entities,
              },
              loading: false,
              loaded: true,
            }
          }

          return item;
        });

        editState = {
          ...editState,
          sections: editSections
        }
      }

      return {
        ...state,
        collection: {
          ...state.collection,
          [collection]: {
            ...collectionState,
            item: {
              ...collectionState.item,
              sections: sections
            }
          }
        },
        edit: editState
      };
    }

    case FETCHING_COLLECTION_SECTION_FAILED: {
      const { collection, section, error } = action.payload as FetchCollectionSectionErrorResponse;

      const collectionState = state.collection[collection];
      if (!collectionState || !collectionState.item) {
        return state;
      }

      const sections = (collectionState.item.sections || []).map((item) => {
        if (item.item?.id === section) {
          return {
            ...item,
            loading: false,
            error: error
          }
        }

        return item;
      });

      return {
        ...state,
        collection: {
          ...state.collection,
          [collection]: {
            ...collectionState,
            item: {
              ...collectionState.item,
              sections: sections
            }
          }
        }
      };
    }

    case ADD_COLLECTION_TO_LIST_REQUEST: {
      const { source, target, collection } = action.payload as AddCollectionToListRequest;

      const id = collection.id;
      if (!id) {
        return state;
      }

      let moveState = state.move[id] || {
        source: source,
        target,
        collection,
        moving: false,
        moved: false,
        error: null
      };

      moveState = {
        ...moveState,
        id: Math.random().toString(36).substring(7),
        type: 'add',
        source,
        target,
        collection,
        moving: true,
        moved: false,
        error: null
      }

      return {
        ...state,
        move: {
          ...state.move,
          [id]: moveState
        }
      }
    }

    case ADDING_COLLECTION_TO_LIST_SUCCEEDED: {
      const { source, target, collection } = action.payload as AddCollectionToListResponse;

      const id = collection.id;
      if (!id) {
        return state;
      }

      const moveState = state.move[id];
      if (!moveState) {
        return state;
      }

      const filtered = Object.keys(state.filtered).reduce((acc: { [key: string]: CollectionItemsState }, key) => {
        const listState = state.filtered[key];
        let items = listState.items;
        const smartList = DEFAULT_COLLECTION_LIST.find((list) => list.id === key);
        if (key === source && !smartList) {
          items = listState.items.filter((item) => {
            return item.id !== id;
          });
        }

        acc[key] = {
          ...listState,
          items
        }

        return acc;
      }, {});

      return {
        ...state,
        filtered,
        move: {
          ...state.move,
          [id]: {
            ...moveState,
            source,
            target,
            moving: false,
            moved: true,
            error: null
          }
        }
      }
    }

    case ADDING_COLLECTION_TO_LIST_FAILED: {
      const { source, target, collection, error } = action.payload as AddCollectionToListErrorResponse;

      const id = collection.id;
      if (!id) {
        return state;
      }

      const moveState = state.move[id];
      if (!moveState) {
        return state;
      }

      return {
        ...state,
        move: {
          ...state.move,
          [id]: {
            ...moveState,
            source,
            target,
            moving: false,
            moved: false,
            error: error
          }
        }
      }
    }

    case REMOVE_COLLECTION_FROM_LIST_REQUEST: {
      const { source, target, collection } = action.payload as RemoveCollectionFromListRequest;

      const id = collection.id;
      if (!id) {
        return state;
      }

      let moveState = state.move[id] || {
        type: 'remove',
        source,
        target,
        collection,
        moving: false,
        moved: false,
        error: null
      };

      moveState = {
        ...moveState,
        id: Math.random().toString(36).substring(7),
        type: 'remove',
        source,
        target,
        collection,
        moving: true,
        moved: false,
        error: null
      }

      return {
        ...state,
        move: {
          ...state.move,
          [id]: moveState
        }
      }
    }

    case REMOVING_COLLECTION_FROM_LIST_SUCCEEDED: {
      const { source, target, collection } = action.payload as RemoveCollectionFromListResponse;

      const id = collection.id;
      if (!id) {
        return state;
      }

      const moveState = state.move[id];
      if (!moveState) {
        return state;
      }

      const filtered = Object.keys(state.filtered).reduce((acc: { [key: string]: CollectionItemsState }, key) => {
        const listState = state.filtered[key];
        let items = listState.items;

        const smartList = DEFAULT_COLLECTION_LIST.find((list) => list.id === key);
        if (key === source && !smartList) {
          items = listState.items.filter((item) => {
            return item.id !== id;
          });
        }

        acc[key] = {
          ...listState,
          items
        }

        return acc;
      }, {});

      return {
        ...state,
        filtered,
        move: {
          ...state.move,
          [id]: {
            ...moveState,
            source,
            target,
            moving: false,
            moved: true,
            error: null
          }
        }
      }
    }

    case REMOVING_COLLECTION_FROM_LIST_FAILED: {
      const { source, target, collection, error } = action.payload as RemoveCollectionFromListErrorResponse;

      const id = collection.id;
      if (!id) {
        return state;
      }

      const moveState = state.move[id];
      if (!moveState) {
        return state;
      }

      return {
        ...state,
        move: {
          ...state.move,
          [id]: {
            ...moveState,
            source,
            target,
            moving: false,
            moved: false,
            error: error
          }
        }
      }
    }

    //Collection List
    case CREATE_COLLECTION_LIST_REQUEST: {
      return {
        ...state,
        lists: {
          ...state.lists,
          creation: {
            ...initialCollectionListCreationState,
            creating: true
          }
        }
      }
    }

    case CREATING_COLLECTION_LIST_SUCCEEDED: {
      const { response } = action.payload as CreateCollectionListResponse;
      const creationState = state.lists.creation;
      if (!creationState) {
        return state;
      }

      let items = state.lists.items || [];
      items = [...items, response];
      items = _.uniqBy(items, 'id');
      items = _.sortBy(items, 'name');

      return {
        ...state,
        lists: {
          ...state.lists,
          items,
          creation: {
            ...creationState,
            creating: false,
            created: true,
            item: response,
            error: null
          }
        }
      }
    }

    case CREATING_COLLECTION_LIST_FAILED: {
      const { error } = action.payload as CreateCollectionListErrorResponse;
      const creationState = state.lists.creation;
      if (!creationState) {
        return state;
      }

      return {
        ...state,
        lists: {
          ...state.lists,
          creation: {
            ...creationState,
            creating: false,
            created: true,
            error: error
          }
        }
      }
    }

    case UPDATE_COLLECTION_LIST_REQUEST: {
      const { id } = action.payload as UpdateCollectionListRequest;

      if (!id) {
        return state;
      }

      let updateState = state.lists.update[id] || initialCollectionListUpdateState;

      updateState = {
        ...updateState,
        id,
        updating: true,
        updated: false,
        item: null,
        error: null
      }

      return {
        ...state,
        lists: {
          ...state.lists,
          update: {
            ...state.lists.update,
            [id]: updateState
          }
        }
      }
    }

    case UPDATING_COLLECTION_LIST_SUCCEEDED: {
      const { response } = action.payload as UpdateCollectionListResponse;

      const id = response.id;
      if (!id) {
        return state;
      }

      const updateState = state.lists.update[id];
      if (!updateState) {
        return state;
      }

      const items = state.lists.items.map((item) => {
        if (item.id === id) {
          return response;
        }

        return item;
      });

      return {
        ...state,
        lists: {
          ...state.lists,
          items,
          update: {
            ...state.lists.update,
            [id]: {
              ...updateState,
              updating: false,
              updated: true,
              item: response,
              error: null
            }
          }
        }
      }
    }

    case UPDATING_COLLECTION_LIST_FAILED: {
      const { id, error } = action.payload as UpdateCollectionListErrorResponse;
      const updateState = state.lists.update[id];
      if (!updateState) {
        return state;
      }

      return {
        ...state,
        lists: {
          ...state.lists,
          update: {
            ...state.lists.update,
            [id]: {
              ...updateState,
              updating: false,
              updated: false,
              error: error
            }
          }
        }
      }
    }

    case DELETE_COLLECTION_LIST_REQUEST: {
      const { id } = action.payload as DeleteCollectionListRequest;
      let deletionState = state.lists.deletion[id] || initialCollectionListDeletionState;

      deletionState = {
        ...deletionState,
        id,
        deleting: true,
        deleted: false,
        error: null
      }

      return {
        ...state,
        lists: {
          ...state.lists,
          deletion: {
            ...state.deletion,
            [id]: deletionState
          }
        }
      }
    }

    case DELETING_COLLECTION_LIST_SUCCEEDED: {
      const { id } = action.payload as DeleteCollectionListResponse;

      const deletionState = state.lists.deletion[id];
      if (!deletionState) {
        return state;
      }

      const items = state.lists.items.filter((item) => {
        return item.id !== id;
      });

      const filtered = _.omit(state.filtered, id);

      return {
        ...state,
        filtered,
        lists: {
          ...state.lists,
          items,
          deletion: {
            ...state.deletion,
            [id]: {
              ...deletionState,
              deleting: false,
              deleted: true,
              error: null
            }
          }
        }
      }
    }

    case DELETING_COLLECTION_LIST_FAILED: {
      const { id, error } = action.payload as UpdateCollectionListErrorResponse;
      const deletionState = state.lists.deletion[id];
      if (!deletionState) {
        return state;
      }

      return {
        ...state,
        lists: {
          ...state.lists,
          deletion: {
            ...state.deletion,
            [id]: {
              ...deletionState,
              deleting: false,
              deleted: false,
              error: error
            }
          }
        }
      }
    }

    case INVALIDATE_COLLECTION_LIST_CREATION_STATE: {
      return {
        ...state,
        lists: {
          ...state.lists,
          creation: null
        }
      }
    }

    case INVALIDATE_COLLECTION_LIST_UPDATE_STATE: {
      return {
        ...state,
        lists: {
          ...state.lists,
          update: {}
        }
      }
    }

    case INVALIDATE_COLLECTION_LIST_DELETION_STATE: {
      return {
        ...state,
        lists: {
          ...state.lists,
          deletion: {}
        }
      }
    }

    case INVALIDATE_COLLECTION_CREATION_STATE: {
      return {
        ...state,
        creation: null
      }
    }

    case INVALIDATE_COLLECTION_UPDATE_STATE: {
      return {
        ...state,
        update: {}
      }
    }

    case INVALIDATE_COLLECTION_DELETION_STATE: {
      return {
        ...state,
        deletion: {}
      }
    }

    case INVALIDATE_COLLECTION_MOVE_STATE: {
      const id = action.payload as string;
      const moveState = _.omit(state.move, id);

      return {
        ...state,
        move: moveState
      }
    }

    //Edit Collection
    case EDIT_COLLECTION_REQUEST: {
      const collection = action.payload as CollectionEditState;

      return {
        ...state,
        edit: collection
      }
    }

    case INVALIDATE_COLLECTION_EDIT_STATE: {
      return {
        ...state,
        edit: null
      }
    }

    case CREATE_COLLECTION_SECTION_REQUEST: {
      const { name } = action.payload as CreateCollectionSectionRequest;

      const collectionState = state.edit;
      if (!collectionState) {
        return state;
      }

      const sectionState = {
        uid: nanoid(6),
        loading: false,
        loaded: true,
        item: {
          name,
          collection: collectionState.id,
          entities: []
        },
        error: null
      }

      let sections = collectionState.sections || [];
      sections = [...sections, sectionState];

      return {
        ...state,
        edit: {
          ...collectionState,
          sections
        }
      };
    }

    case REMOVE_COLLECTION_SECTION_REQUEST: {
      const { index } = action.payload as RenameCollectionSectionRequest;

      const collectionState = state.edit;
      if (!collectionState) {
        return state;
      }

      let sections = collectionState.sections || [];
      sections = sections.filter((item, i) => i !== index);

      return {
        ...state,
        edit: {
          ...collectionState,
          sections
        }
      };
    }

    case UPDATE_COLLECTION_SECTION_REQUEST: {
      const { index, name } = action.payload as RenameCollectionSectionRequest;

      const collectionState = state.edit;
      if (!collectionState) {
        return state;
      }

      let sections = collectionState.sections || [];
      sections = sections.map((item, i) => {
        if (i === index) {
          return {
            ...item,
            item: {
              ...item.item,
              name,
              entities: item.item?.entities || []
            }
          }
        }

        return item;
      });

      return {
        ...state,
        edit: {
          ...collectionState,
          sections
        }
      };
    }

    case MOVE_COLLECTION_SECTION_REQUEST: {
      const { from, to } = action.payload as MoveCollectionSectionRequest;

      const collectionState = state.edit;
      if (!collectionState) {
        return state;
      }

      let sections = collectionState.sections || [];
      sections = [...sections];
      sections.splice(to, 0, sections.splice(from, 1)[0]);

      return {
        ...state,
        edit: {
          ...collectionState,
          sections
        }
      };
    }

    case COLLECTION_ADD_ASSETS: {
      const { section, assets, addAt } = action.payload as AddAssetToCollectionRequest;
      const collectionState = state.edit;
      if (!collectionState) {
        return state;
      }

      let sections = collectionState.sections || [];
      let existingAssets = (collectionState.entities || []);

      if (section !== undefined) {        
        sections = sections.map((item, index) => {
          if (index === section) {
            let existing = item.item?.entities || [];
            const contactCardExistsInTargetSection = getContactCardExists(existing);

            let assetsToBeAdded = [...assets];
            
            if (contactCardExistsInTargetSection) {
              assetsToBeAdded = assets.filter(
                (asset) => _.get(asset, "type", "ASSET") !== "CONTACT_CARD",
              );
            }

            if (addAt === 'start') {
              existing = [...assetsToBeAdded, ...existing];
            } else {
              existing = [...existing, ...assetsToBeAdded];
            }

            existing = _.uniqBy(existing, 'id');

            return {
              ...item,
              item: {
                ...item.item,
                name: _.get(item, 'item.name') || '',
                entities: existing
              }
            }
          }

          return item;
        });
      } else {
        if (addAt === 'start') {
          existingAssets = [...assets, ...existingAssets];
        } else {
          existingAssets = [...existingAssets, ...assets];
        }

        existingAssets = _.uniqBy(existingAssets, 'id');
      }

      return {
        ...state,
        edit: {
          ...collectionState,
          sections: sections,
          entities: existingAssets
        }
      };
    }

    case COLLECTION_REMOVE_ASSETS: {
      const { section, asset } = action.payload as RemoveAssetFromCollectionRequest;
      const collectionState = state.edit;
      if (!collectionState) {
        return state;
      }

      let sections = collectionState.sections || [];
      if (section !== undefined) {
        sections = sections.map((item, index) => {
          if (index === section) {
            return {
              ...item,
              item: {
                ...item.item,
                name: item.item?.name || '',
                entities: item.item?.entities?.filter((a) => a.id !== asset) || []
              }
            }
          }

          return item;
        });
      }

      const entities = (collectionState.entities || []).filter((a) => a.id !== asset);

      return {
        ...state,
        edit: {
          ...collectionState,
          sections: sections,
          entities: entities
        }
      };
    }

    case COLLECTION_REARRANGE_ASSETS: {
      const { section, from, to } = action.payload as ArrangeAssetInCollectionRequest;
      const collectionState = state.edit;
      if (!collectionState) {
        return state;
      }

      let sections = collectionState.sections || [];
      let assets = collectionState.entities || [];
      if (section !== undefined && sections.length > 0) {
        sections = sections.map((item, index) => {
          if (index === section) {
            let assets = item.item?.entities || [];
            assets = [...assets];

            const element = assets[from];
            assets.splice(from, 1);
            assets.splice(to, 0, element);

            assets = assets.map((asset, index) => {
              return {
                ...asset,
                order: index
              }
            });

            return {
              ...item,
              item: {
                ...item.item,
                name: item.item?.name || '',
                entities: assets
              }
            }
          }

          return item;
        });
      } else {
        assets = [...assets];
        const element = assets[from];
        assets.splice(from, 1);
        assets.splice(to, 0, element);

        assets = assets.map((asset, index) => ({
          ...asset,
          order: index
        }));
      }

      return {
        ...state,
        edit: {
          ...collectionState,
          sections: sections,
          entities: assets
        }
      };
    }

    case COLLECTION_UPDATE_ASSET_METADATA: {
      const { collection, section, updates } = action.payload as UpdateAssetMetadataRequest
      let collectionState = state.collection[collection]?.item;
      if (!collectionState) {
        return state;
      }

      let collectionEditState = state.edit;

      const applyUpdates = (assets: CollectionAsset[], updates: { id: string, metadata: any }[]) => {
        return assets.map((asset) => {
          const update = updates.find((a) => a.id === asset.id);
          if (update) {
            return {
              ...asset,
              metadata: {
                ..._.get(asset, "metadata", {}),
                ..._.get(update, "metadata", {}),
              }
            }
          }

          return asset;
        });
      }

      const applyState = (entityState: CollectionEditState, updates: { id: string, metadata: any }[]) => {
        let sections = entityState.sections || [];
        let updatedAssets = entityState.entities || [];
        if (section !== undefined) {
          sections = sections.map((item, index) => {
            if (index === section) {
              let assets = item.item?.entities || [];
              assets = [...assets];

              return {
                ...item,
                item: {
                  ...item.item,
                  name: item.item?.name || '',
                  entities: applyUpdates(assets, updates)
                }
              }
            }

            return item;
          });
        } else {
          updatedAssets = applyUpdates(updatedAssets, updates);
        }

        return {
          ...entityState,
          sections: sections,
          entities: updatedAssets
        };
      }

      if (collectionEditState) {
        collectionEditState = applyState(collectionEditState, updates);
      } else {
        collectionState = applyState(collectionState, updates);
      }

      return {
        ...state,
        collection: {
          ...state.collection,
          [collection]: {
            ...state.collection[collection],
            item: collectionState
          },
        },
        edit: collectionEditState
      };
    }

    case COLLECTION_UPDATE_PROPERTY: {
      const { property, value } = action.payload as { property: string, value: any };
      const collectionState = state.edit;
      if (!collectionState) {
        return state;
      }

      return {
        ...state,
        edit: {
          ...collectionState,
          [property]: value
        }
      };
    }

    case TOGGLE_COLLECTION_TEMPLATE_REQUEST:
    case TOGGLE_COLLECTION_LIVE_REQUEST:
    case TOGGLE_COLLECTION_INTERNAL_REQUEST: {
      const { id } = action.payload as ToggleCollectionTemplateRequest | ToggleCollectionInternalRequest | ToggleCollectionLiveRequest;
      let updateState = state.update[id] || initialCollectionUpdateState;

      updateState = {
        ...updateState,
        id,
        updating: true,
        updated: false,
        item: null,
        error: null
      }

      return {
        ...state,
        update: {
          ...state.lists.update,
          [id]: updateState
        }
      }
    }

    case TOGGLING_COLLECTION_STATE_SUCCEEDED: {
      const { response } = action.payload as ToggleCollectionStateResponse;
      const id = response.id;
      if (!id) {
        return state;
      }

      const updateState = state.update[id];
      if (!updateState) {
        return state;
      }

      const collectionState = {
        ...state.collection[id],
        item: {
          ...state.collection[id].item,
          ..._.omit(response, 'sections'),
          sections: state.collection[id].item?.sections || [],
        }
      };

      const items = state.items.map((item) => {
        if (item.id === id) {
          return response;
        }

        return item;
      });

      const filtered = Object.keys(state.filtered).reduce((acc: { [key: string]: CollectionItemsState }, key) => {
        const listState = state.filtered[key];
        const items = listState.items.map((item) => {
          if (item.id === id) {
            return response;
          }

          return item;
        });

        acc[key] = {
          ...listState,
          items
        }

        return acc;
      }, {});

      return {
        ...state,
        items,
        filtered,
        update: {
          ...state.update,
          [id]: {
            ...updateState,
            updating: false,
            updated: true,
            item: response,
            error: null
          }
        },
        collection: {
          ...state.collection,
          [id]: collectionState
        },
      }
    }

    case TOGGLING_COLLECTION_STATE_FAILED: {
      const { id, error } = action.payload as ToggleCollectionStateErrorResponse;
      const updateState = state.update[id];
      if (!updateState) {
        return state;
      }

      return {
        ...state,
        update: {
          ...state.update,
          [id]: {
            ...updateState,
            updating: false,
            updated: false,
            error: error
          }
        }
      }
    }

    case FETCH_COLLECTION_SHARED_CONTACTS_REQUEST: {
      const { collection } = action.payload as FetchCollectionSharedContactRequest;

      const collectionState = state.collection[collection];
      if (!collectionState) {
        return state;
      }

      const recipientsState = collectionState.recipients || {
        loading: true,
        loaded: false,
        error: null,
        items: [],
        pagination: {
          currentPage: -1,
          totalPages: 0,
          recordsPerPage: 10,
          totalRecordsCount: 0,
          hasMoreItems: false
        }
      }

      return {
        ...state,
        collection: {
          ...state.collection,
          [collection]: {
            ...collectionState,
            recipients: {
              ...recipientsState,
              loading: true
            }
          }
        }
      }
    }

    case FETCHING_COLLECTION_SHARED_CONTACTS_SUCCEEDED: {
      const { collection, response } = action.payload as FetchCollectionSharedContactResponse;

      const collectionState = state.collection[collection];
      if (!collectionState) {
        return state;
      }

      const existingItems = collectionState.recipients?.items || [];
      let records = response.records;
      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }

      records = _.uniqBy(records, 'email');

      return {
        ...state,
        collection: {
          ...state.collection,
          [collection]: {
            ...collectionState,
            recipients: {
              loading: false,
              loaded: true,
              error: null,
              items: records,
              pagination: {
                currentPage: response.currentPage,
                totalPages: response.totalPages,
                recordsPerPage: response.recordsPerPage,
                totalRecordsCount: response.totalRecordsCount,
                hasMoreItems: response.currentPage < response.totalPages - 1
              }
            }
          }
        }
      }
    }

    case FETCHING_COLLECTION_SHARED_CONTACTS_FAILED: {
      const { collection, error } = action.payload as FetchCollectionSharedContactErrorResponse;

      const collectionState = state.collection[collection];
      if (!collectionState) {
        return state;
      }

      return {
        ...state,
        collection: {
          ...state.collection,
          [collection]: {
            ...collectionState,
            recipients: {
              ...collectionState.recipients,
              loading: false,
              loaded: false,
              error: error,
            } as CollectionSharedContactsState
          }
        }
      }
    }

    case FETCH_COLLECTION_USER_ACCESS_REQUEST:
    case FETCH_COLLECTION_COLLABORATORS_REQUEST: {
      const { collection } = action.payload as FetchCollectionCollaboratorsRequest;

      const collectionState = state.collection[collection];
      if (!collectionState) {
        return state;
      }

      return {
        ...state,
        collection: {
          ...state.collection,
          [collection]: {
            ...collectionState,
            collaborators: {
              ...collectionState.collaborators,
              items: collectionState.collaborators?.items || [],
              loading: true,
              loaded: false,
              error: null,
            }
          }
        }
      }
    }

    case FETCHING_COLLECTION_COLLABORATORS_SUCCEDDED: {
      const { collection, response } = action.payload as FetchCollectionCollaboratorsResponse;

      const collectionState = state.collection[collection];
      if (!collectionState) {
        return state;
      }

      const existingItems = collectionState.collaborators?.items || [];
      let records = response.records;
      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }

    records = _.orderBy(records, [
      "entityType",
      item => {
        const entityType = _.get(item, 'entityType');
        if (entityType === 'user') {
          return item.user?.enabled === true ? 0 : 1; 
        }
        return 0;
      },
      item => {
        const entityType = _.get(item, 'entityType');
        let name = "";
        if (entityType === 'user') {
          name = item.user?.name || "";
        } else {
          name = item.userGroup?.name || "";
        }
        return _.toLower(name);
     }
    ], ['desc', 'asc', 'asc']);

      return {
        ...state,
        collection: {
          ...state.collection,
          [collection]: {
            ...collectionState,
            collaborators: {
              loading: false,
              loaded: true,
              error: null,
              items: records,
              pagination: {
                currentPage: response.currentPage,
                totalPages: response.totalPages,
                recordsPerPage: response.recordsPerPage,
                totalRecordsCount: response.totalRecordsCount,
                hasMoreItems: response.currentPage < response.totalPages - 1
              }
            }
          }
        }
      }
    }

    case FETCHING_COLLECTION_USER_ACCESS_SUCCEDDED: {
      const { collection, response } = action.payload as FetchUserAccessResponse;

      const collectionState = state.collection[collection];
      if (!collectionState) {
        return state;
      }

      //records = _.uniqBy(records, 'email');

      return {
        ...state,
        collection: {
          ...state.collection,
          [collection]: {
            ...collectionState,
            collaborators: {
              loading: false,
              loaded: true,
              error: null,
              items: response,
              pagination: {
                currentPage: 0,
                totalPages: 1,
                recordsPerPage: _.size(response),
                totalRecordsCount: _.size(response),
                hasMoreItems: false
              }
            }
          }
        }
      }
    }

    case FETCHING_COLLECTION_USER_ACCESS_FAILED:
    case FETCHING_COLLECTION_COLLABORATORS_FAILED: {
      const { collection, error } = action.payload as FetchCollectionCollaboratorsErrorResponse;

      const collectionState = state.collection[collection];
      if (!collectionState) {
        return state;
      }

      return {
        ...state,
        collection: {
          ...state.collection,
          [collection]: {
            ...collectionState,
            collaborators: {
              ...collectionState.collaborators,
              loading: false,
              loaded: false,
              error: error,
            } as CollectionCollaboratorsState
          }
        }
      }
    }

    case UPDATE_COLLECTION_COLLABORATORS_REQUEST: {
      const { collection } = action.payload as UpdateCollectionCollaboratorsRequest;

      const collectionState = state.collection[collection];
      if (!collectionState) {
        return state;
      }

      return {
        ...state,
        collection: {
          ...state.collection,
          [collection]: {
            ...collectionState,
            collaborators: {
              ...collectionState.collaborators,
              updating: true,
              updated: false,
            } as CollectionCollaboratorsState
          }
        }
      }
    }

    case UPDATING_COLLECTION_COLLABORATORS_SUCCEDDED: {
      const { collection, readAll, writeAll } = action.payload as UpdateCollectionCollaboratorsResponse;

      const collectionState = state.collection[collection];
      if (!collectionState) {
        return state;
      }

      return {
        ...state,
        collection: {
          ...state.collection,
          [collection]: {
            ...collectionState,
            item:{
              ...collectionState.item,
              readAll,
              writeAll
            },
            collaborators: {
              ...collectionState.collaborators,
              updating: false,
              updated: true,
            } as CollectionCollaboratorsState
          }
        }
      }
    }

    case UPDATING_COLLECTION_COLLABORATORS_FAILED: {
      const { collection, error } = action.payload as UpdateCollectionCollaboratorsErrorResponse;

      const collectionState = state.collection[collection];
      if (!collectionState) {
        return state;
      }

      return {
        ...state,
        collection: {
          ...state.collection,
          [collection]: {
            ...collectionState,
            collaborators: {
              ...collectionState.collaborators,
              updating: false,
              updated: false,
              error: error
            } as CollectionCollaboratorsState
          }
        }
      }
    }

    case INVALIDATE_COLLECTION_COLLABORATORS: {
      const collection = action.payload as string;

      const collectionState = state.collection[collection];
      if (!collectionState) {
        return state;
      }

      return {
        ...state,
        collection: {
          ...state.collection,
          [collection]: {
            ...collectionState,
            collaborators: undefined
          }
        }
      }
    }

    case ADDING_ASSETS_TO_COLLECTION_ON_SERVER_SUCCEDDED: {
      const { response } = action.payload as AddAssetToCollectionResponse;
      if (!response || !response.id) {
        return state;
      }

      const collectionState = state.collection[response.id];
      if (!collectionState) {
        return state;
      }

      const sections = (response.sections || []).map((section): CollectionSectionState => {
        return {
          uid: nanoid(6),
          loading: true,
          loaded: false,
          item: {
            ...section,
            collection: response.id
          },
          error: null
        }
      }) as CollectionSectionState[];

      return {
        ...state,
        collection: {
          ...state.collection,
          [response.id]: {
            loading: false,
            loaded: true,
            item: {
              ...response,
              sections: sections || []
            },
            error: null
          }
        }
      }
    }

    case ADD_ASSETS_TO_SECTION_ON_SERVER_SUCCEDDED: {
      const { collection, response } = action.payload as AddAssetToCollectionResponse;

      if (!response || !response.id) {
        return state;
      }

      const collectionState = state.collection[collection];
      if (!collectionState) {
        return state;
      }

      const sections = (collectionState.item?.sections || []).map((item) => {
        if (item.item?.id === response.id) {
          return {
            ...item,
            item: {
              ...item.item,
              collection: collection,
              entities: response.entities,
            },
            loading: false,
            loaded: true,
          } as CollectionSectionState
        }

        return item;
      });

      return {
        ...state,
        collection: {
          ...state.collection,
          [collection]: {
            ...collectionState,
            item: {
              ...collectionState.item,
              sections: sections
            }
          }
        }
      };
    }

    case FETCH_ASSETS_FROM_UNCATEGORIZED_COLLECTION: {
      return {
        ...state,
        uncategorized: {
          ...state.uncategorized,
          loading: true,
          error: null,
        }
      }
    }

    case FETCHING_ASSETS_FROM_UNCATEGORIZED_COLLECTION_SUCCEDDED: {
      const { response } = action.payload as FetchAssetFromUncategorisedCollectionResponse;

      const existingItems: UncategorizedAssetState[] = state.uncategorized.items;

      let records = (response.records || []).map((item) => {
        return {
          id: item.id,
          asset: item,
          analytics: {
            loading: true,
            loaded: false,
            error: null,
            item: null
          }
        } as UncategorizedAssetState;
      });

      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }

      records = _.uniqBy(records, 'id');
      records = sortUncategorizedAssets(records, state.uncategorized.sortBy);

      return {
        ...state,
        uncategorized: {
          ...state.uncategorized,
          loading: false,
          items: records,
          pagination: {
            currentPage: response.currentPage,
            totalPages: response.totalPages,
            recordsPerPage: response.recordsPerPage,
            totalRecordsCount: response.totalRecordsCount,
            hasMoreItems: response.currentPage < response.totalPages - 1
          },
        }
      }
    }

    case FETCHING_ASSETS_FROM_UNCATEGORIZED_COLLECTION_FAILED: {
      const { error } = action.payload as FetchAssetFromUncategorisedCollectionErrorResponse;

      return {
        ...state,
        uncategorized: {
          ...state.uncategorized,
          loading: false,
          error: error,
          items: []
        }
      }
    }

    case SORT_UNCATEGORIZED_COLLECTION_CHILDREN: {
      const sortBy = action.payload as SortUncategorizedAssetsRequest;
      let existingItems: UncategorizedAssetState[] = state.uncategorized.items || [];
      existingItems = sortUncategorizedAssets(existingItems, sortBy);

      return {
        ...state,
        uncategorized: {
          ...state.uncategorized,
          items: existingItems,
          sortBy: sortBy
        }
      }
    }

    case FETCHING_ASSET_ANALYTICS_REQUEST: {
      const { assetId } = action.payload as FetchAssetAnalyticsRequest;

      const existingItems = state.uncategorized.items || [];
      const records = existingItems.map((item) => {
        if (item.id === assetId) {
          return {
            ...item,
            analytics: {
              ...item.analytics,
              loading: true,
              loaded: false,
            }
          }
        }

        return item;
      });

      return {
        ...state,
        uncategorized: {
          ...state.uncategorized,
          items: records
        }
      }
    }

    case FETCHING_ASSET_ANALYTICS_SUCCEEDED: {
      const { assetId, response } = action.payload as FetchAssetAnalyticsResponse;

      const existingItems = state.uncategorized.items || [];
      const records = existingItems.map((item) => {
        if (item.id === assetId) {
          return {
            ...item,
            analytics: {
              loading: false,
              loaded: true,
              item: response,
              error: null
            }
          }
        }

        return item;
      });

      return {
        ...state,
        uncategorized: {
          ...state.uncategorized,
          items: records
        }
      }
    }

    case FETCHING_ASSET_ANALYTICS_FAILED: {
      const { assetId, error } = action.payload as FetchAssetAnalyticsErrorResponse;

      const existingItems = state.uncategorized.items || [];
      const records = existingItems.map((item) => {
        if (item.id === assetId) {
          return {
            ...item,
            analytics: {
              loading: false,
              loaded: false,
              error: error,
              item: null
            }
          }
        }

        return item;
      });

      return {
        ...state,
        uncategorized: {
          ...state.uncategorized,
          items: records
        }
      }
    }

    case DELETE_UNCATEGORIZED_ASSET: {
      return {
        ...state,
        uncategorized: {
          ...state.uncategorized,
          deletion: {
            deleting: true,
            deleted: false,
            error: null
          }
        }
      }
    }

    case DELETING_UNCATEGORIZED_ASSET_SUCCEEDED: {
      const { response } = action.payload as DeleteUncategorizedAssetResponse;

      const existingItems = state.uncategorized.items || [];
      const records = existingItems.filter((item) => {
        const found = _.find(response, { id: item.id }) != null;
        return !found;
      });

      return {
        ...state,
        uncategorized: {
          ...state.uncategorized,
          items: records,
          deletion: {
            deleting: false,
            deleted: true,
            error: null
          }
        }
      }
    }

    case DELETING_UNCATEGORIZED_ASSET_FAILED: {
      const { error } = action.payload as DeleteUncategorizedAssetErrorResponse;

      return {
        ...state,
        uncategorized: {
          ...state.uncategorized,
          deletion: {
            deleting: false,
            deleted: false,
            error: error
          }
        }
      }
    }

    case INVALIDATE_UNCATEGORIZED_ASSET_DELETION: {
      return {
        ...state,
        uncategorized: {
          ...state.uncategorized,
          deletion: {
            deleting: false,
            deleted: false,
            error: null
          }
        }
      }
    }

    case REPLACE_SIMILAR_ASSET_SUCCEEDED: {
      
      const response = action.payload as ReplaceSimilarAssetResponse;

      const newAssetId = response.newAssetId;

      const editCollection = state.edit;
      const existingAssets = editCollection?.entities || [];

      var existingAsset = response.existingAsset;

      if (_.isEmpty(editCollection)) {
        return state;
      }

      if (_.size(editCollection.sections) > 0) {
        const updatedSections = _.map(editCollection.sections, (section) => {
          const existingSectionAssets = section?.item?.entities || [];

          const hasAsset = _.some(
            existingSectionAssets,
            (asset) => asset.id === newAssetId,
          );

          if (hasAsset) {
            const filteredAssets = existingSectionAssets.map(asset => {
              if (asset.id === newAssetId) {
                return {
                  asset: existingAsset,
                  id: existingAsset.id,
                  type: 'ASSET',
                  metadata: existingAsset.metadata,
                  order: asset.order,
                };
              }
              return asset;
            });

            return {
              ...section,
              item: {
                ...section.item,
                assets: _.uniqBy(filteredAssets, "id"),
              },
            };
          }

          return section;
        });

        return {
          ...state,
          edit: {
            ...state.edit,
            sections: updatedSections as CollectionSectionState[],
          },
        };
      } else {
        const newAsset = _.find(
          existingAssets,
          (asset) => asset.id === newAssetId,
        );

        if (newAsset) {
          const filteredAssets = existingAssets.map(asset => {
            if (asset.id === newAssetId) {
              return {
               asset: existingAsset,
               id: existingAsset.id,
                type: 'ASSET',
               metadata: existingAsset.metadata,
               order: asset.order,
              };
            }
            return asset;
          });

          return {
            ...state,
            edit: {
              ...editCollection,
              entities: _.uniqBy(filteredAssets, "id"),
            },
          };
        }
      }

      return {
        ...state,
      };
    }


    default: {
      return state
    }
  }
}